/******/ (() => { // webpackBootstrap
class CartUpdate extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('textarea');
    this.saveButton = this.querySelector('[data-cart-update-save]');
    this.characterCount = this.querySelector('[data-cart-character-count]');
    this.openButton = this.querySelector('[data-cart-update-open]');
    this.openButtonText = this.querySelector('[data-cart-update-open-text]');
    this.collapseButton = this.querySelector('[data-cart-update-collapse]');
    this.handleInputChange = this.handleInputChange.bind(this);
    this.handleInput = this.handleInput.bind(this);
    this.handleSave = this.handleSave.bind(this);
    this.handleOpen = this.handleOpen.bind(this);
    this.handleCollapse = this.handleCollapse.bind(this);
    this.isSaving = false;
    this.savedValue = this.input?.value || '';
  }

  connectedCallback() {
    window.wetheme.webcomponentRegistry.register({key: 'component-cart-update'});
    if (!this.input) return;
    this.input.addEventListener('change', this.handleInputChange);
    this.input.addEventListener('input', this.handleInput);
    this.updateCharacterCount();
    this.updateOpenButtonText();

    this.saveButton?.addEventListener('click', this.handleSave);
    this.openButton?.addEventListener('click', this.handleOpen);
    this.collapseButton?.addEventListener('click', this.handleCollapse);

    this.closest('cart-update-modal')
      ?.querySelector('button[data-cart-update-close]')
      ?.addEventListener('click', this.handleSave);
  }

  handleInputChange(e) {
    this.save(e.target.value);
  }

  handleInput() {
    this.updateCharacterCount();
  }

  async handleSave() {
    await this.save(this.input?.value || '', true);
    this.collapse();
  }

  handleOpen() {
    if (!this.hasAttribute('data-collapsible')) return;

    this.setAttribute('open', '');
    this.openButton?.setAttribute('aria-expanded', 'true');
    this.input?.focus();
  }

  handleCollapse() {
    this.collapse();
  }

  collapse() {
    if (!this.hasAttribute('data-collapsible')) return;

    this.removeAttribute('open');
    this.openButton?.setAttribute('aria-expanded', 'false');
    this.updateOpenButtonText();
  }

  async save(value, force = false) {
    value = this.limitValue(value);
    if (this.input && this.input.value !== value) {
      this.input.value = value;
      this.updateCharacterCount();
    }

    if (this.isSaving || (!force && value === this.savedValue)) return;

    this.isSaving = true;
    /* ===== Update the cart ===== */
    const name = this.input.name;

    const bodyObj = await this.fetchBody(name, value);
    if (!bodyObj) {
      this.isSaving = false;
      return;
    }

    try {
      await fetch(window.routes.cart_update_url, {
        ...this.fetchConfig(),
        body: JSON.stringify(bodyObj),
        keepalive: true,
      });

      if (name === 'attributes[gift_note]') {
        await this.syncCardMessageProperty(value);
      }

      this.savedValue = value;
    } finally {
      this.isSaving = false;
    }
  }

  async syncCardMessageProperty(value) {
    const cartResponse = await fetch(`${window.routes.cart_url}.js`);
    if (!cartResponse.ok) return;

    const cart = await cartResponse.json();
    const primaryItem = cart.items?.[0];
    if (!primaryItem) return;

    // A public line item property is rendered by Shopify below the item in checkout.
    const properties = { ...(primaryItem.properties || {}) };
    const message = value.trim();

    if (message) {
      properties['Card message'] = message;
    } else {
      delete properties['Card message'];

      // Shopify cannot replace line item properties with an empty object.
      if (!Object.keys(properties).length) {
        properties['Card message'] = '';
      }
    }

    const separator = window.routes.cart_change_url.includes('?') ? '&' : '?';
    await fetch(`${window.routes.cart_change_url}${separator}app=pickeasy`, {
      ...this.fetchConfig(),
      body: JSON.stringify({
        id: primaryItem.key,
        properties,
      }),
    });
  }

  async fetchBody(name, value) {
    /* ===== Cart note update ===== */
    if (name === 'note') {
      return { note: value };
    }

    /* ===== Cart attribute update ===== */
    const match = name.match(/^attributes\[(\w+)\]$/);
    if (match) {
      const response = await fetch(`${window.routes.cart_url}.js`);
      if (!response.ok) return null;

      const cart = await response.json();

      return {
        attributes: {
          ...(cart.attributes || {}),
          [match[1]]: value,
        },
      };
    }

    return null;
  }

  fetchConfig(type = 'json') {
    /* ===== Create the fetch configuration object ===== */
    return {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
    };
  }

  limitValue(value) {
    const limit = Number.parseInt(this.dataset.characterLimit, 10);
    return Number.isFinite(limit) && limit > 0 ? value.slice(0, limit) : value;
  }

  updateCharacterCount() {
    if (!this.characterCount || !this.input) return;

    this.characterCount.textContent = `${this.limitValue(this.input.value).length} / ${this.dataset.characterLimit}`;
  }

  updateOpenButtonText() {
    if (!this.openButtonText || !this.input) return;

    this.openButtonText.textContent = this.input.value.trim()
      ? this.dataset.editLabel
      : this.dataset.addLabel;
  }
}

customElements.define('cart-update', CartUpdate);
/******/ })()
;
